import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows['2xl']};
  max-width: ${props => props.size === 'small' ? '400px' : props.size === 'large' ? '800px' : '600px'};
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 0 1.5rem;
  border-bottom: ${props => props.divider ? `1px solid ${props.theme.colors.border.light}` : 'none'};
  margin-bottom: ${props => props.divider ? '1rem' : '0'};
`;

const ModalTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const ModalBody = styled.div`
  padding: ${props => props.noPadding ? '0' : '1.5rem'};
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
  border-top: ${props => props.divider ? `1px solid ${props.theme.colors.border.light}` : 'none'};
  margin-top: ${props => props.divider ? '1rem' : '0'};
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const ModalComponent = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'medium',
  showCloseButton = true,
  closeOnOverlayClick = true,
  className,
  ...props
}) => {
  const { theme } = useTheme();
  
  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer
        theme={theme}
        size={size}
        className={className}
        {...props}
      >
        {(title || showCloseButton) && (
          <ModalHeader theme={theme} divider={!!children}>
            {title && <ModalTitle theme={theme}>{title}</ModalTitle>}
            {showCloseButton && (
              <CloseButton theme={theme} onClick={onClose}>
                ×
              </CloseButton>
            )}
          </ModalHeader>
        )}
        
        {children && (
          <ModalBody theme={theme}>
            {children}
          </ModalBody>
        )}
        
        {footer && (
          <ModalFooter theme={theme} divider={!!children}>
            {footer}
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ModalComponent;

